import { useEffect, useState, useCallback, useRef } from 'react';
import { formatDuration } from '../TimeFormatter';

export const useTimer = (
    initialIsActive = false,
    initialElapsedTime = 0
) => {
    const [elapsedTime, setElapsedTime] = useState(initialElapsedTime);
    const [isActive, setIsActive] = useState(initialIsActive);

    const intervalRef = useRef(null);

    // Последнее время от сервера
    const lastServerTimeRef = useRef(initialElapsedTime);
    const elapsedTimeRef = useRef(initialElapsedTime);

    const isActiveRef = useRef(initialIsActive);
    // Момент старта локального отсчёта
    const startTimestampRef = useRef(null);

    useEffect(() => {
        elapsedTimeRef.current = elapsedTime;
    }, [elapsedTime]);

    useEffect(() => {
        isActiveRef.current = isActive;
    }, [isActive]);

    const startTimer = useCallback(() => {
        if (isActiveRef.current) return;

        startTimestampRef.current =
            Date.now() - lastServerTimeRef.current * 1000;

        setIsActive(true);
    }, []);

    const stopTimer = useCallback(() => {
        setIsActive(false);

        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
    }, []);

    const resetTimer = useCallback((newInitialTime = 0) => {
        stopTimer();

        setElapsedTime(newInitialTime);

        lastServerTimeRef.current = newInitialTime;
        startTimestampRef.current = null;
    }, [stopTimer]);

    const syncWithServerTime = useCallback(
        (serverElapsedTime) => {
            lastServerTimeRef.current = serverElapsedTime;

            const drift = Math.abs(
                elapsedTimeRef.current - serverElapsedTime
            );

            if (drift > 2) {
                elapsedTimeRef.current = serverElapsedTime;
                setElapsedTime(serverElapsedTime);

                if (isActive) {
                    startTimestampRef.current =
                        Date.now() - serverElapsedTime * 1000;
                }
            }
        },
        [isActive]
    );

    useEffect(() => {
        if (!isActive) return;

        intervalRef.current = setInterval(() => {
            if (!startTimestampRef.current) return;

            const seconds = Math.floor(
                (Date.now() - startTimestampRef.current) / 1000
            );

            setElapsedTime(seconds);
        }, 1000);

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [isActive]);

    return {
        elapsedTime,
        formattedTime: formatDuration(elapsedTime),
        startTimer,
        stopTimer,
        resetTimer,
        syncWithServerTime,
        isActive
    };
};